import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react"
import { NavLink } from "react-router-dom"
import { useStoreState, useStoreActions, StateMapper } from "easy-peasy"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPersonSwimming,
  faGears,
  faPersonSwimmingWater,
} from "@fortawesome/pro-solid-svg-icons"

import {
  faAddressCard,
  faCalendar,
  faReceipt,
  faCalendarDays,
  faChartMixed,
  faCreditCard,
  faCalendarCheck,
  faHome,
  faSliders,
  faRankingStar,
  faUsersLine,
  faGraduationCap,
  faLifeRing,
  faStore,
  faChalkboardUser,
  faPowerOff,
  faPeopleRoof,
  faBullhorn,
  faBrowser,
  faHouse,
  faUsers,
  faCircleInfo,
  faTowerBroadcast,
} from "@fortawesome/pro-regular-svg-icons"

import { Menu, Tag, Badge } from "antd"
import { AppDataStore } from "../../appData/types"
import { gen, ISwimmer, IClub, swimminglyApi } from "../utils"
import { LeagueNameFit } from "../../styles/LeagueNameFit"
import { useFeatureAccess } from "../FeatureAccessContext"
import { FeatureKey } from "../../constants/featureAccess"

const faIcon = (icon: any) => <FontAwesomeIcon icon={icon} fixedWidth />

interface ISidebarProps {
  isClosed: boolean
  changeClose: (val: boolean) => void
  isMobile: boolean
  setUserLockout: (value: boolean) => void
}

interface MenuConfigItem {
  key: string
  label: string
  icon?: any
  roles?: number[]
  allowInactive?: boolean
  external?: string
  action?: (e?: any) => void
  conditional?: () => boolean
}

/**
 * ClubNameDisplay
 * - Renders the full club long name by default.
 * - If the rendered text overflows (i.e. would be ellipsized), switches to the short name (abbrev).
 * - Always exposes the full name in the title attribute for hover.
 *
 * Uses measurement (scrollWidth > clientWidth) and listens for window resize to re-check.
 */
const ClubNameDisplay: React.FC<{ full: string; short: string }> = ({ full, short }) => {
  const ref = useRef<HTMLSpanElement | null>(null)
  const [useShort, setUseShort] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => {
      // measure overflow
      setUseShort(el.scrollWidth > el.clientWidth)
    }
    // run initially
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [full, short])

  // ensure ellipsis styles so overflow measurement is meaningful
  const style: React.CSSProperties = {
    display: "inline-block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    maxWidth: "100%",
    fontWeight: 900,
  }

  return (
    <span ref={ref} title={full} style={style}>
      {useShort ? short : full}
    </span>
  )
}

export default function Sidebar({
  isClosed,
  changeClose,
  isMobile,
  setUserLockout,
}: ISidebarProps) {
  const pathname = (window.location && window.location.pathname) || "/"
  const user = useStoreState((s: StateMapper<AppDataStore>) => s.user)
  const impersonateClub = useStoreState((s: StateMapper<AppDataStore>) => s.aliasedClub) as IClub | undefined
  const triggerUsersSwimmersRefresh = useStoreState(
    (s: StateMapper<AppDataStore>) => s.triggerUsersSwimmersRefresh,
  )

  const [usersSwimmers, setUsersSwimmers] = useState<ISwimmer[]>([])
  const [showCurriculumSidebar, setShowCurriculumSidebar] = useState(false)
  const [websitesPaymentsNewCount, setWebsitesPaymentsNewCount] = useState<number>(0)
  const [hasLiveMeets, setHasLiveMeets] = useState(false)
  const { isGuestClub, isFeatureLocked, openLockedModal } = useFeatureAccess()

  // older antd in this project — use CSS var fallback for background
  const colorBgContainer = "var(--primaryblue, #001529)"

  const setAliasedClub = useStoreActions((a: any) => a.setAliasedClub)
  const [leagueName, setLeagueName] = useState<string>(() => {
    const existing =
      (impersonateClub as any)?.leagueName ||
      (impersonateClub as any)?.league_name ||
      (impersonateClub as any)?.league?.name ||
      ""
    const trimmed = String(existing).trim()
    return trimmed.length > 0 ? trimmed : "League Account"
  })

  useEffect(() => {
    let mounted = true

    // only fetch for roles 2 or 3
    if (!user || !(user.role === 2 || user.role === 3)) return

    // fast path: club already includes the league name
    const existing =
      (impersonateClub as any)?.leagueName ||
      (impersonateClub as any)?.league_name ||
      (impersonateClub as any)?.league?.name
    if (existing && String(existing).trim()) {
      if (mounted) setLeagueName(String(existing).trim())
      return
    }

    // derive league id (prefer impersonateClub then user)
    const leagueId =
      (impersonateClub as any)?.leagueId ??
      (impersonateClub as any)?.league_id ??
      (user as any)?.leagueId ??
      (user as any)?.league_id

    if (!leagueId) {
      if (mounted) setLeagueName("League Account")
      return
    }

    const url = gen(`/api/getLeague/${leagueId}`)
    swimminglyApi
      .get(url)
      .then((data: any) => {
        if (!mounted) return
        const name = data?.name || data?.league?.name || data?.leagueName || data?.league_name
        const trimmed = name && String(name).trim()
        const final = trimmed || "League Account"
        setLeagueName(final)
        // cache into store so subsequent renders avoid network call;
        // only update if the leagueName actually changed to prevent
        // unnecessary state churn.
        if (trimmed && impersonateClub && (impersonateClub as any)?.leagueName !== trimmed) {
          try {
            setAliasedClub({ ...(impersonateClub as any), leagueName: trimmed })
          } catch (err) {
            /* noop */
          }
        }
      })
      .catch((err: any) => {
        console.error("[Sidebar] getLeague error:", err)
        if (mounted) setLeagueName("League Account")
      })

    return () => {
      mounted = false
    }
  }, [
    user?.role,
    impersonateClub,
    impersonateClub?.leagueId,
    impersonateClub?.league_id,
    (impersonateClub as any)?.leagueName,
    user?.leagueId,
    setAliasedClub,
  ])
  

  useEffect(() => {
    if (!user) {
      setShowCurriculumSidebar(false)
      return
    }
    if (user.role === 2) {
      setShowCurriculumSidebar(true)
      return
    }
    if ((user.role === 3 || user.role === 4) && user.clubId) {
      swimminglyApi
        .get(`/api/club-curriculums/${user.clubId}`)
        .then((data) => {
          setShowCurriculumSidebar(Array.isArray(data.assignments) && data.assignments.length > 0)
        })
        .catch(() => setShowCurriculumSidebar(false))
      return
    }
    setShowCurriculumSidebar(false)
  }, [user])

  useEffect(() => {
    if (!user) {
      setWebsitesPaymentsNewCount(0)
      return
    }

    let mounted = true

    const loadCount = async () => {
      try {
        const query = `?userId=${encodeURIComponent(String(user.userId))}`
        const resp = await swimminglyApi.get(`/api/activity-feed/summary${query}`)
        const payload: any =
          (resp as any)?.data && !Array.isArray(resp)
            ? (resp as any).data
            : (resp as any)
        const value =
          typeof payload?.countsByCategory?.billing === "number"
            ? payload.countsByCategory.billing
            : typeof payload?.summary?.unreadByCategory?.billing === "number"
              ? payload.summary.unreadByCategory.billing
              : 0
        if (mounted) {
          setWebsitesPaymentsNewCount(value)
        }
      } catch (err) {
        console.error("[Sidebar] websites payments newCount error:", err)
        if (mounted) {
          setWebsitesPaymentsNewCount(0)
        }
      }
    }

    loadCount()

    const handleViewed = () => {
      loadCount()
    }

    window.addEventListener("swimmingly:activity-feed:refetch", handleViewed)
    window.addEventListener("websitesPaymentsViewed", handleViewed)

    return () => {
      mounted = false
      window.removeEventListener("swimmingly:activity-feed:refetch", handleViewed)
      window.removeEventListener("websitesPaymentsViewed", handleViewed)
    }
  }, [user])

  useEffect(() => {
    if (user?.userId) {
      swimminglyApi
        .get(`/api/getAllSwimmersOfGuardian/${user.userId}`)
        .then((data) => {
          if (!data.swimmers) setUsersSwimmers([])
          else setUsersSwimmers(data.swimmers)
        })
        .catch(() => setUsersSwimmers([]))
    } else {
      setUsersSwimmers([])
    }
  }, [user?.userId, triggerUsersSwimmersRefresh])

  const logout = async (e?: any) => {
    e?.preventDefault()
    // Dynamically imported so Firebase's SDK isn't pulled into the main bundle for users
    // who never visit Live Meets (Sidebar itself isn't lazy-loaded).
    const { signOutOfLiveMeetsFirebase } = await import("../utils/liveMeetsFirebase")
    await signOutOfLiveMeetsFirebase()
    await swimminglyApi.get(gen("/api/logout"))
    // if mobile, close drawer
    if (isMobile) changeClose(true)
    window.location.reload()
  }

  const manageUsersKey = user && user.role === 2 ? "/app/admin/manage_admin_users" : "/app/admin/manage_users/"
  const isSwimminglyAdmin = !!user && user.role === 2

  // subtle "LIVE" indicator on the Live Meets nav item when at least one meet is in progress.
  // Dynamically imported so Firebase isn't pulled into the main bundle for admins who never
  // visit Live Meets (mirrors the lazy-import pattern already used in logout() above).
  useEffect(() => {
    if (!isSwimminglyAdmin) return
    let unsubscribe: (() => void) | undefined
    let cancelled = false

    ;(async () => {
      try {
        const [{ signInToLiveMeetsFirebase, getLiveMeetsFirestore }, { collection, query, where, limit, onSnapshot }] =
          await Promise.all([import("../utils/liveMeetsFirebase"), import("firebase/firestore")])
        await signInToLiveMeetsFirebase()
        if (cancelled) return
        const liveMeetsQuery = query(
          collection(getLiveMeetsFirestore(), "meets"),
          where("in_progress_status", "==", "inProgress"),
          where("is_deleted", "==", false),
          limit(1),
        )
        unsubscribe = onSnapshot(
          liveMeetsQuery,
          (snapshot) => {
            if (!cancelled) setHasLiveMeets(!snapshot.empty)
          },
          () => {
            if (!cancelled) setHasLiveMeets(false)
          },
        )
      } catch (_err) {
        // Live Meets indicator is a nice-to-have — fail silently rather than disrupt the sidebar
        if (!cancelled) setHasLiveMeets(false)
      }
    })()

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [isSwimminglyAdmin])

  const menuConfig = useMemo<MenuConfigItem[]>(
    () => [
      { key: "/app/parentMeets", label: "My Meet Schedule", icon: faCalendar, roles: [2, 3, 4, 5], allowInactive: false  },
      { key: "/app/admin/liveMeets", label: "Live Meets", icon: faTowerBroadcast, roles: [2] },

      // impersonate club section (rendered only when impersonateClub truthy)
      { key: "/app/landingPage", label: "My Registrations", icon: faAddressCard, roles: [2, 3, 4, 5] },
      { key: "/app/club/admin", label: "Manage Club", icon: faHome, roles: [2, 3, 4] },
      { key: "/app/club/manageclubusers", label: "Club Admins", icon: faPeopleRoof, roles: [2, 3, 4] },
      { key: "/app/club/manageroster", label: "Manage Roster", icon: faPersonSwimming, roles: [2, 3, 4] },
      { key: "/app/parentHome", label: "My Swimmers", icon: faPersonSwimmingWater, roles: [2, 3, 4, 5] },
      { key: "/app/club/meetschedule2", label: "Meet Schedule", icon: faCalendarDays, roles: [2, 3, 4] },
      { key: "/app/admin/curriculums", label: "Curriculum", icon: faChalkboardUser, roles: [2, 3, 4], conditional: () => showCurriculumSidebar },
      { key: "/app/websites", label: "Websites", icon: faBrowser, roles: [2, 3, 4] },

      { key: "/app/club/reports", label: "Reports", icon: faChartMixed, roles: [2, 3, 4] },
      { key: "/app/billing", label: "My Payments", icon: faReceipt, roles: [2, 3, 4, 5]  },
      { key: "/app/club/billing", label: "Club Billing", icon: faCreditCard, roles: [3, 4], conditional: () => user?.clubId === impersonateClub?.clubId && !isFeatureLocked(FeatureKey.billing) && !isGuestClub },

      // admin / league
      { key: "/app/admin/club/seasons", label: "Registration", icon: faCalendarCheck, roles: [2, 3, 4] },
      { key: "/app/admin/manage_league", label: "Leagues", icon: faSliders, roles: [2, 3] },
      { key: "/app/admin/club/", label: "Clubs", icon: faBullhorn, roles: [2, 3] },
      { key: "/app/leaderboard", label: "Leaderboard", icon: faRankingStar, roles: [2, 3] },
      {
        key: manageUsersKey,
        label: "League Admins",
        icon: faUsersLine,
        roles: [2, 3],
      },
      { key: "/app/admin/swimminglyadmin", label: "Control Panel", icon: faGears, roles: [2] },

      // external / misc
      { key: "external:academy", label: "Swimmingly Academy", icon: faGraduationCap, external: "https://academy.swimmingly.app" },
      { key: "external:support", label: "Help Center", icon: faLifeRing, external: "https://support.swimmingly.app" },
      { key: "external:store", label: "Store", icon: faStore, external: "https://store.swimmingly.app" },

      // logout
      { key: "logout", label: "Logout", icon: faPowerOff, action: logout },
    ],
    [showCurriculumSidebar, user, impersonateClub],
  )

  // Filter items by role and conditional functions
  const visibleItems = menuConfig.filter((m) => {
    if (m.roles && !user) return false
    if (m.roles && user && !m.roles.includes(user.role)) return false
    if (m.conditional && typeof m.conditional === "function" && !m.conditional()) return false
    return true
  })

  type LocalMenuItem = {
    key: string
    icon?: React.ReactNode
    labelNode: React.ReactNode
  }

  // Build menu items compatible with older antd Menu API
  const items: LocalMenuItem[] = visibleItems.map((m) => {
    const baseStyle = {
      color: "inherit",
      display: "inline-flex",
      alignItems: "center" as const,
      gap: 8,
    }

    const labelNode =
      m.external ? (
        <a href={m.external} target="_blank" rel="noopener noreferrer" style={baseStyle}>
          <span className="menu-label">{m.label}</span>
        </a>
      ) : m.key === "logout" ? (
        <a
          onClick={(e) => {
            e?.preventDefault()
            m.action && m.action(e)
          }}
          style={baseStyle}
        >
          <span className="menu-label">{m.label}</span>
        </a>
      ) : m.key === "/app/club/reports" && isFeatureLocked(FeatureKey.reports) ? (
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); openLockedModal(FeatureKey.reports) }}
          style={baseStyle}
        >
          <span className="menu-label">{m.label}</span>
        </a>
      ) : (
        <NavLink
          to={String(m.key)}
          onClick={(e: any) => {
            // guard inactive users
            if (m.roles && user && !m.allowInactive && user.isActive !== true && m.roles.includes(user.role)) {
              e.preventDefault()
              setUserLockout(true)
              return
            }
            // if mobile overlay, close after navigation
            if (isMobile) changeClose(true)
          }}
          style={baseStyle}
        >
          <span className="menu-label">
            {m.label}
            {m.key === "/app/websites" && (
              <Tag color="#f0be1b" style={{ marginLeft: 8, fontWeight: "bold" }}>
                NEW
              </Tag>
            )}
            {m.key === "/app/admin/liveMeets" && hasLiveMeets && (
              <span className="sidebar-live-dot" aria-label="A meet is live right now" title="A meet is live right now" />
            )}
            {m.key === "/app/billing" && websitesPaymentsNewCount > 0 && (
              <Badge
                count={websitesPaymentsNewCount}
                size="small"
                overflowCount={99}
                style={{ marginLeft: 8, backgroundColor: "#fa541c" }}
              />
            )}
          </span>
        </NavLink>
      )

    return {
      key: m.key,
      icon: faIcon(m.icon),
      labelNode,
    }
  })

  // If impersonateClub is false, hide the impersonate-club-only subset
  const finalItems = useMemo(() => {
    if (!impersonateClub) {
      return items.filter(
        (it) =>
          ![
            "/app/club/admin",
            "/app/club/manageclubusers",
            "/app/club/manageroster",
            "/app/club/meetschedule2",
            "/app/club/reports",
            "/app/club/billing",
            "/app/admin/curriculums",
          ].includes(String(it.key)),
      )
    }
    return items
  }, [items, impersonateClub])

  const finalItemsNoLogout = finalItems.filter((it) => String(it.key) !== "logout")
  const logoutMenuItem = finalItems.find((it) => String(it.key) === "logout")

  // --- Grouping: build My Account submenu at top ---
  const accountKeys = ["/app/landingPage", "/app/parentHome", "/app/parentMeets", "/app/billing"]
  const accountItems = accountKeys
    .map((k) => finalItemsNoLogout.find((it) => it.key === k))
    .filter(Boolean) as LocalMenuItem[]

  // --- New grouping: Club Account submenu ---
  const clubKeys = [
    "/app/admin/club/seasons", // Registration (force to top)
    "/app/club/admin",
    "/app/club/manageclubusers",
    "/app/club/manageroster",
    "/app/club/meetschedule2",
    "/app/club/billing",
    "/app/club/reports",
  ]
  // Preserve the explicit order defined in clubKeys
  const clubItems = clubKeys
    .map((k) => finalItemsNoLogout.find((it) => it.key === k))
    .filter(Boolean) as LocalMenuItem[]

  // --- New grouping: Admin Account submenu ---
  const adminKeys = ["/app/admin/manage_league", "/app/admin/club/", "/app/leaderboard", manageUsersKey]
  const adminItems = finalItemsNoLogout.filter((it) => adminKeys.includes(it.key))

  // --- Learn & Support grouping (external links) ---
  const learnKeys = ["external:academy", "external:support", "external:store"]
  const learnItems = finalItemsNoLogout.filter((it) => learnKeys.includes(it.key))

  // control panel (separate top-level item)
  const CONTROL_PANEL_KEY = "/app/admin/swimminglyadmin"
  const controlPanelItem = finalItemsNoLogout.find((it) => String(it.key) === CONTROL_PANEL_KEY)

  // remaining items exclude account, club, admin, learn groups, live meets, and control panel
  const LIVE_MEETS_KEY = "/app/admin/liveMeets"
  const liveMeetsItem = finalItemsNoLogout.find((it) => String(it.key) === LIVE_MEETS_KEY)
  const otherItems = finalItemsNoLogout.filter(
    (it) =>
      !accountKeys.includes(it.key) &&
      !clubKeys.includes(it.key) &&
      !adminKeys.includes(it.key) &&
      !learnKeys.includes(it.key) &&
      String(it.key) !== LIVE_MEETS_KEY &&
      String(it.key) !== CONTROL_PANEL_KEY,
  )

  // Controlled open keys so submenu state follows routing and user interactions
  const [openKeys, setOpenKeys] = useState<string[]>([])

  // derive safe display names and parse first name (moved up so antdMenuItems can use them)
  const rawUserName =
    (user as any)?.name ??
    (user as any)?.fullName ??
    (user as any)?.firstName ??
    (user as any)?.first_name ??
    ""
  const userFirstName = rawUserName && String(rawUserName).trim() ? String(rawUserName).trim().split(/\s+/)[0] : "My"
  const userDisplayName = rawUserName && String(rawUserName).trim() ? String(rawUserName).trim() : ""

  // prefer long name if present; short name used as fallback/abbrev
  const clubLongCandidate =
    impersonateClub?.nameLong ??
    (impersonateClub as any)?.longName ??
    (impersonateClub as any)?.name_long ??
    (impersonateClub as any)?.fullName ??
    impersonateClub?.name
  const clubLongName = clubLongCandidate && String(clubLongCandidate).trim() ? String(clubLongCandidate).trim() : "Club"
  const clubShortName = impersonateClub?.name ?? "Club"

  // map items -> antd MenuProps.items (preserve grouping, dividers and user info)
  const antdMenuItems = (() => {
    const arr: any[] = []

    // Live Meets is the most prominent nav item — always first, with dividers above and below
    if (liveMeetsItem) {
      arr.push({ type: "divider", key: "div-livemeets-top" })
      arr.push({
        key: String(liveMeetsItem.key),
        icon: liveMeetsItem.icon,
        label: liveMeetsItem.labelNode,
      })
      arr.push({ type: "divider", key: "div-livemeets" })
    }

    // top divider to match other sidebar separators, above My Account —
    // skipped when Live Meets already added its own divider right above this
    if (accountItems.length > 0 && !liveMeetsItem) {
      arr.push({ type: "divider", key: "div-top-root" })
    }

    if (accountItems.length > 0) {
      arr.push({
        key: "myAccount",
        label: <span className="sidebar-submenu-label"><span className="sidebar-submenu-title">My Account</span></span>,
        icon: faIcon(faPersonSwimming),
        children: [
          ...(userDisplayName || user?.email
            ? [
                {
                  key: "myAccount-identity",
                  disabled: true,
                  className: "submenu-identity-header",
                  label: (
                    <span className="submenu-identity-text submenu-identity-text-user">
                      {userDisplayName || user?.email}
                    </span>
                  ),
                },
              ]
            : []),
          ...accountItems.map((it) => ({
            key: String(it.key),
            icon: it.icon,
            label: it.labelNode,
          })),
        ],
      })
    }

    if (clubItems.length > 0) {
      arr.push({
        key: "clubAccount",
        label: <span className="sidebar-submenu-label"><span className="sidebar-submenu-title">Club Account</span></span>,
        icon: faIcon(faHouse),
        children: [
          {
            key: "clubAccount-identity",
            disabled: true,
            className: "submenu-identity-header",
            label: (
              <span className="submenu-identity-text">
                <LeagueNameFit name={clubLongName} />
              </span>
            ),
          },
          ...clubItems.map((it) => ({
            key: String(it.key),
            icon: it.icon,
            label: it.labelNode,
          })),
        ],
      })
    }

    if (!isSwimminglyAdmin && adminItems.length > 0) {
      arr.push({
        key: "adminAccount",
        label: (
          <span className="sidebar-submenu-label">
            <span className="sidebar-submenu-title">League Account</span>
          </span>
        ),
        icon: faIcon(faUsers),
        children: [
          {
            key: "adminAccount-identity",
            disabled: true,
            className: "submenu-identity-header",
            label: (
              <span className="submenu-identity-text">
                <LeagueNameFit name={leagueName} />
              </span>
            ),
          },
          ...adminItems.map((it) => ({
            key: String(it.key),
            icon: it.icon,
            label: it.labelNode,
          })),
        ],
      })
    }

    if (learnItems.length > 0) {
      arr.push({
        key: "learnSupport",
        label: "Learn & Support",
        icon: faIcon(faCircleInfo),
        children: learnItems.map((it) => ({
          key: String(it.key),
          icon: it.icon,
          label: it.labelNode,
        })),
      })
    }

    // other top-level items
    otherItems.forEach((it) =>
      arr.push({
        key: String(it.key),
        icon: it.icon,
        label: it.labelNode,
      })
    )

    // divider then control panel if present
    if (controlPanelItem) {
      arr.push({ type: "divider", key: "div-controlpanel" })

      if (isSwimminglyAdmin) {
        const controlPanelChildren: any[] = []

        // Control Panel home
        controlPanelChildren.push({
          key: String(controlPanelItem.key),
          icon: controlPanelItem.icon,
          label: controlPanelItem.labelNode,
        })

        const controlPanelGroupKeys = [
          "/app/admin/club/",
          "/app/admin/manage_league",
          manageUsersKey,
          "/app/leaderboard",
        ]

        controlPanelGroupKeys.forEach((k) => {
          const found = finalItemsNoLogout.find((it) => it.key === k)
          if (found) {
            controlPanelChildren.push({
              key: String(found.key),
              icon: found.icon,
              label: found.labelNode,
            })
          }
        })

        arr.push({
          key: "controlPanelGroup",
          label: "Control Panel",
          icon: controlPanelItem.icon,
          children: controlPanelChildren,
        })
      } else {
        arr.push({
          key: String(controlPanelItem.key),
          icon: controlPanelItem.icon,
          label: controlPanelItem.labelNode,
        })
      }
    }

    // divider then user info block
    arr.push({ type: "divider", key: "div-userinfo-top" })
    if (user) {
      arr.push({
        key: "sidebar-user-info",
        disabled: true,
        icon: null,
        className: "sidebar-user-info-item",
        style: {
          cursor: "default",
          background: "transparent",
          paddingTop: 12,
          paddingBottom: 12,
          color: "var(--white)",
          opacity: 1,
          display: "flex",
          alignItems: "center",
          height: "auto",
        },
        label: (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <span style={{ color: "rgba(255,255,255,0.85)", opacity: 0.85, marginTop: 0, fontSize: 12, fontStyle: "italic" }}>User:</span>
            <span style={{ color: "rgba(255,255,255,0.85)", opacity: 0.85, marginTop: 4, fontSize: 10 }}>{user.email}</span>
            {user.role !== 5 && (
              <>
                <span style={{ color: "rgba(255,255,255,0.85)", opacity: 0.85, marginTop: 10, fontSize: 12, fontStyle: "italic" }}>Logged in as:</span>
                <span style={{ color: "rgba(255,255,255,0.85)", opacity: 0.85, marginTop: 4, fontSize: 12 }}>
                  {impersonateClub && <ClubNameDisplay full={clubLongName} short={clubShortName} />}
                </span>
              </>
            )}
          </div>
        ),
      })
    }

    // final divider + logout
    arr.push({ type: "divider", key: "div-userinfo-bottom" })
    if (logoutMenuItem) {
      arr.push({
        key: String(logoutMenuItem.key),
        icon: logoutMenuItem.icon,
        label: logoutMenuItem.labelNode,
      })
    }

    return arr
  })()

  // Map submenu key -> child route keys
  const submenuKeyMap: { [subKey: string]: string[] } = {
    myAccount: accountKeys,
    clubAccount: clubKeys,
    adminAccount: adminKeys,
    learnSupport: learnKeys,
  }

  if (isSwimminglyAdmin) {
    // For Swimmingly Admins we don't render the adminAccount submenu; use the Control Panel group instead
    delete submenuKeyMap.adminAccount
    submenuKeyMap.controlPanelGroup = [
      CONTROL_PANEL_KEY,
      "/app/admin/club/",
      "/app/admin/manage_league",
      manageUsersKey,
      "/app/leaderboard",
    ]
  }

  // derive open keys from pathname (open the submenu that contains the active route)
  useEffect(() => {
    const matching = Object.entries(submenuKeyMap).find(([subKey, routes]) =>
      routes.some((r) => String(r) === pathname),
    )
    if (matching) setOpenKeys([String(matching[0])])
    else setOpenKeys([])
  }, [pathname])

  // clear open keys when sider collapsed; restore none (state could be saved/restored if desired)
  useEffect(() => {
    if (isClosed) {
      setOpenKeys([])
    }
  }, [isClosed])

  // Accept React.Key[] from antd and convert to string[] for internal state
  const onOpenChange = (nextOpenKeys: React.Key[]) => {
    const next = (nextOpenKeys || []).map(String)
    const latestOpenKey = next.find((k) => !openKeys.includes(k))
    if (!latestOpenKey) {
      setOpenKeys(next)
      return
    }
    if (["myAccount", "clubAccount", "adminAccount", "learnSupport", "controlPanelGroup"].includes(latestOpenKey)) {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : [])
    } else {
      setOpenKeys(next)
    }
  }

  const onSubmenuTitleClick = (subKey: string) => {
    setOpenKeys((prev) => (prev.includes(subKey) ? prev.filter((k) => k !== subKey) : [subKey]))
  }

  return (
    <>
      <div
        className="sidebar-scroll"
        style={{
          height: isMobile ? "calc(100vh - 64px)" : "calc(100% - 64px)",
          overflow: "auto",
          background: colorBgContainer,
        }}
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[pathname]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          inlineIndent={16}
          style={{ background: colorBgContainer }}
          items={antdMenuItems}
        />
      </div>
    </>
  )
}